import { Link } from 'react-router-dom'
import type { StoredSession } from '../api/session.js'
import { useI18n } from '../i18n/index.js'
import { LegalNoticeDe, PrivacyDe } from './LegalDe.js'
import { LegalNoticeEs, PrivacyEs } from './LegalEs.js'
import { LegalNoticeHi, PrivacyHi } from './LegalHi.js'
import { LEGAL, LegalShell, Mail, TranslationNotice } from './LegalShell.js'

export { LEGAL }

const LEGAL_NOTICES = { fr: LegalNoticeFr, en: LegalNoticeEn, es: LegalNoticeEs, de: LegalNoticeDe, hi: LegalNoticeHi }
const PRIVACY = { fr: PrivacyFr, en: PrivacyEn, es: PrivacyEs, de: PrivacyDe, hi: PrivacyHi }

export function LegalNoticePage({ session }: { session: StoredSession | null }) {
  const Page = LEGAL_NOTICES[useI18n().locale]
  return <Page session={session} />
}

export function PrivacyPage({ session }: { session: StoredSession | null }) {
  const Page = PRIVACY[useI18n().locale]
  return <Page session={session} />
}

function LegalNoticeFr({ session }: { session: StoredSession | null }) {
  return (
    <LegalShell session={session} eyebrow="Informations légales" title="Mentions légales">
      <h2>Éditeur du site</h2>
      <p>
        Le site {LEGAL.domain} (« {LEGAL.siteName} ») est édité par un particulier, à titre non professionnel. Conformément à l’article 6
        III-2 de la loi n° 2004-575 du 21 juin 2004 pour la confiance dans l’économie numérique, l’éditeur ne publie pas ses coordonnées
        personnelles ; celles-ci ont été communiquées à l’hébergeur.
      </p>
      <p>
        Contact : <Mail />
      </p>

      <h2>Hébergement</h2>
      <p>
        Le site est hébergé par {LEGAL.host.name}, {LEGAL.host.address.fr} —{' '}
        <a href={LEGAL.host.website} rel="noopener noreferrer" target="_blank">
          {LEGAL.host.website.replace('https://', '')}
        </a>
        .
      </p>

      <h2>Propriété intellectuelle</h2>
      <p>
        Le code, les illustrations, le logo et les textes du site sont la propriété de l’éditeur, sauf mention contraire. Le moteur d’échecs
        s’appuie sur la bibliothèque libre chess.js (licence BSD-2-Clause) ; les polices Fraunces et Instrument Sans sont distribuées sous
        licence SIL Open Font License.
      </p>
      <p>
        Chess-Uno est un jeu indépendant. Il n’est ni affilié, ni approuvé, ni sponsorisé par Mattel, titulaire de la marque UNO®.
      </p>

      <h2>Données personnelles</h2>
      <p>
        Le traitement des données des joueurs est décrit dans la <Link to="/confidentialite">politique de confidentialité</Link>.
      </p>
    </LegalShell>
  )
}

function PrivacyFr({ session }: { session: StoredSession | null }) {
  return (
    <LegalShell session={session} eyebrow="Vos données" title="Politique de confidentialité">
      <p className="lead-p">
        {LEGAL.siteName} collecte le strict nécessaire pour vous permettre de jouer et d’être classé. Aucune publicité, aucun outil tiers de
        mesure d’audience, aucun cookie de suivi.
      </p>

      <h2>Responsable du traitement</h2>
      <p>
        L’éditeur du site, particulier (voir les <Link to="/mentions-legales">mentions légales</Link>), joignable à <Mail />.
      </p>

      <h2>Données traitées</h2>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Donnée</th>
              <th>Usage</th>
              <th>Conservation</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Pseudo</td>
              <td>Identifier votre compte et vous afficher aux autres joueurs.</td>
              <td>Jusqu’à la suppression du compte.</td>
            </tr>
            <tr>
              <td>Mot de passe</td>
              <td>Connexion. Il n’est jamais stocké en clair : seule une empreinte salée (scrypt) est conservée.</td>
              <td>Jusqu’à la suppression du compte.</td>
            </tr>
            <tr>
              <td>Jetons de session</td>
              <td>
                Rester connecté. Le jeton est gardé dans le stockage local de votre navigateur ; le serveur n’en conserve qu’une empreinte.
              </td>
              <td>Jusqu’à la déconnexion ou la suppression du compte.</td>
            </tr>
            <tr>
              <td>Parties</td>
              <td>Coups, cartes piochées et journal de chaque partie, pour la reprise après déconnexion et l’arbitrage.</td>
              <td>Jusqu’à la suppression du compte.</td>
            </tr>
            <tr>
              <td>Résultats classés</td>
              <td>Classement Elo, statistiques, bilans des confrontations directes.</td>
              <td>Jusqu’à la suppression du compte, puis conservés sans votre pseudo pour ne pas fausser le classement des autres.</td>
            </tr>
            <tr>
              <td>Tournois</td>
              <td>Nom du tournoi, pseudos des participants, tableau et résultats des matchs, pour organiser le tournoi.</td>
              <td>Jusqu’à la suppression du compte.</td>
            </tr>
            <tr>
              <td>Avis et suggestions</td>
              <td>
                Ce que vous choisissez d’envoyer : message, note de 1 à 5, contact facultatif (e-mail ou pseudo de messagerie), ainsi que votre pseudo si vous
                êtes connecté, la page, la langue, le type d’écran et le navigateur utilisés. Lus par l’éditeur pour améliorer le jeu et vous répondre si
                vous le demandez.
              </td>
              <td>12 mois, ou jusqu’à votre demande de suppression.</td>
            </tr>
            <tr>
              <td>Origine des visites</td>
              <td>
                Savoir si les visiteurs viennent d’Instagram, de Google ou d’ailleurs : site de provenance (nom de domaine seulement), étiquettes de
                campagne du lien, page d’arrivée sans paramètres ni codes d’invitation, langue, type d’écran, et si la visite a mené à une création
                de compte. Aucune adresse IP, aucun navigateur détaillé ni identifiant durable n’est enregistré, et rien n’est recoupé avec votre
                compte. Aucune mesure si votre navigateur envoie « Ne pas me suivre » ou le signal Global Privacy Control.
              </td>
              <td>13 mois.</td>
            </tr>
            <tr>
              <td>Adresse IP</td>
              <td>Limiter les tentatives de connexion et l’envoi d’avis (protection contre les attaques). Gardée en mémoire, jamais enregistrée par le jeu.</td>
              <td>Une minute pour la connexion, dix minutes pour les avis. Les journaux techniques de l’hébergeur suivent sa propre politique.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>Bases légales</h2>
      <ul>
        <li>
          <strong>Exécution du service</strong> que vous demandez en créant un compte : compte, parties, classement.
        </li>
        <li>
          <strong>Intérêt légitime</strong> : sécurité du service (limitation des tentatives de connexion et des envois d’avis), amélioration du
          jeu à partir des avis que vous choisissez d’envoyer, et mesure statistique de l’origine des visites. Vous pouvez vous y opposer à tout
          moment en activant « Ne pas me suivre » dans votre navigateur.
        </li>
      </ul>

      <h2>Ce qui est public</h2>
      <p>
        Votre pseudo, votre classement Elo et votre palier, vos statistiques, vos résultats classés et vos bilans face à face apparaissent dans
        le classement et sur votre profil, visibles de tous. Les parties contre l’IA ne sont pas classées. Le nom d’un tournoi, les pseudos de ses
        participants, son tableau et ses résultats sont visibles de toute personne qui en connaît le code. Vos avis ne sont jamais publiés.
      </p>

      <h2>Stockage dans votre navigateur</h2>
      <p>
        Le site n’utilise pas de cookies. Il enregistre dans le stockage local du navigateur votre jeton de session, pour vous garder
        connecté, ainsi que vos préférences d’affichage (langue, son) et un petit compteur qui évite de vous demander trop souvent votre avis
        après une partie : ce stockage est strictement nécessaire au service et ne requiert donc pas de consentement. Le stockage de session
        de l’onglet garde, jusqu’à sa fermeture, un identifiant aléatoire de la visite pour ne la compter qu’une fois ; il n’est pas conservé
        d’une visite à l’autre.
      </p>

      <h2>Destinataires et sous-traitants</h2>
      <p>Vos données ne sont ni vendues ni partagées à des fins commerciales. Elles sont traitées par :</p>
      <ul>
        <li>
          <strong>{LEGAL.host.name}</strong> (États-Unis) : hébergement du serveur et de la base de données ;
        </li>
        <li>
          <strong>Cloudflare, Inc.</strong> (États-Unis) : gestion du nom de domaine et, le cas échéant, acheminement du trafic ;
        </li>
        <li>
          <strong>Telegram</strong> (messagerie) et <strong>Resend, Inc.</strong> (États-Unis, envoi d’e-mails), le cas échéant : acheminement à
          l’éditeur d’une notification de chaque avis reçu.
        </li>
      </ul>
      <p>
        Ces transferts hors de l’Union européenne sont encadrés par les garanties prévues par le RGPD (clauses contractuelles types de la
        Commission européenne ou cadre de protection des données UE–États-Unis, selon le prestataire).
      </p>

      <h2>Sécurité</h2>
      <p>
        Connexions chiffrées (HTTPS), mots de passe et jetons stockés sous forme d’empreintes, limitation des tentatives de connexion, accès à
        la base de données restreint à l’hébergement.
      </p>

      <h2>Vos droits</h2>
      <p>
        Vous disposez d’un droit d’accès, de rectification, d’effacement, de limitation, de portabilité et d’opposition. Pour les exercer — en
        particulier pour supprimer votre compte — écrivez à <Mail /> depuis une adresse permettant de vous identifier, en indiquant
        votre pseudo. Une réponse vous est apportée dans un délai d’un mois.
      </p>
      <p>
        Vous pouvez également adresser une réclamation à la CNIL (
        <a href="https://www.cnil.fr" rel="noopener noreferrer" target="_blank">
          cnil.fr
        </a>
        ).
      </p>
    </LegalShell>
  )
}

function LegalNoticeEn({ session }: { session: StoredSession | null }) {
  return (
    <LegalShell session={session} eyebrow="Legal information" title="Legal notice">
      <TranslationNotice original="/mentions-legales" before="This English translation is provided for convenience. In case of discrepancy, the" link="French version" after="prevails." />

      <h2>Publisher</h2>
      <p>
        The website {LEGAL.domain} (“{LEGAL.siteName}”) is published by a private individual, on a non-professional basis. In accordance
        with article 6 III-2 of French law no. 2004-575 of 21 June 2004 on confidence in the digital economy, the publisher does not publish
        their personal details; these have been provided to the hosting provider.
      </p>
      <p>
        Contact: <Mail />
      </p>

      <h2>Hosting</h2>
      <p>
        The website is hosted by {LEGAL.host.name}, {LEGAL.host.address.en} —{' '}
        <a href={LEGAL.host.website} rel="noopener noreferrer" target="_blank">
          {LEGAL.host.website.replace('https://', '')}
        </a>
        .
      </p>

      <h2>Intellectual property</h2>
      <p>
        The code, illustrations, logo and texts of the website are the property of the publisher, unless stated otherwise. The chess engine
        relies on the open-source chess.js library (BSD-2-Clause license); the Fraunces and Instrument Sans fonts are distributed under the
        SIL Open Font License.
      </p>
      <p>Chess-Uno is an independent game. It is not affiliated with, endorsed or sponsored by Mattel, owner of the UNO® trademark.</p>

      <h2>Personal data</h2>
      <p>
        How player data is processed is described in the <Link to="/en/privacy">privacy policy</Link>.
      </p>
    </LegalShell>
  )
}

function PrivacyEn({ session }: { session: StoredSession | null }) {
  return (
    <LegalShell session={session} eyebrow="Your data" title="Privacy policy">
      <TranslationNotice original="/confidentialite" before="This English translation is provided for convenience. In case of discrepancy, the" link="French version" after="prevails." />
      <p className="lead-p">
        {LEGAL.siteName} collects only what is needed to let you play and be ranked. No advertising, no third-party audience
        measurement tool, no tracking cookie.
      </p>

      <h2>Data controller</h2>
      <p>
        The publisher of the website, a private individual (see the <Link to="/en/legal-notice">legal notice</Link>), reachable at <Mail />.
      </p>

      <h2>Data processed</h2>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Data</th>
              <th>Purpose</th>
              <th>Retention</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Username</td>
              <td>Identify your account and show you to other players.</td>
              <td>Until the account is deleted.</td>
            </tr>
            <tr>
              <td>Password</td>
              <td>Signing in. It is never stored in clear text: only a salted hash (scrypt) is kept.</td>
              <td>Until the account is deleted.</td>
            </tr>
            <tr>
              <td>Session tokens</td>
              <td>Staying signed in. The token is kept in your browser’s local storage; the server only keeps a hash of it.</td>
              <td>Until you sign out or the account is deleted.</td>
            </tr>
            <tr>
              <td>Games</td>
              <td>Moves, cards drawn and log of each game, to resume after a disconnection and to settle disputes.</td>
              <td>Until the account is deleted.</td>
            </tr>
            <tr>
              <td>Ranked results</td>
              <td>Elo rating, statistics, head-to-head records.</td>
              <td>Until the account is deleted, then kept without your username so as not to distort other players’ rankings.</td>
            </tr>
            <tr>
              <td>Tournaments</td>
              <td>Tournament name, participants’ usernames, bracket and match results, to run the tournament.</td>
              <td>Until the account is deleted.</td>
            </tr>
            <tr>
              <td>Feedback</td>
              <td>
                What you choose to send: message, 1–5 score, optional contact (email or messaging handle), plus your username if you are signed in, and the
                page, language, screen type and browser used. Read by the publisher to improve the game and to answer you if you ask for it.
              </td>
              <td>12 months, or until you ask for its deletion.</td>
            </tr>
            <tr>
              <td>Where visits come from</td>
              <td>
                Knowing whether visitors come from Instagram, Google or elsewhere: the referring site (domain name only), the campaign tags of the link,
                the landing page without parameters or invite codes, language, screen type, and whether the visit led to an account being created. No
                IP address, detailed browser or lasting identifier is recorded, and nothing is matched with your account. Nothing is measured if your
                browser sends “Do Not Track” or the Global Privacy Control signal.
              </td>
              <td>13 months.</td>
            </tr>
            <tr>
              <td>IP address</td>
              <td>Limiting sign-in attempts and feedback submissions (protection against attacks). Kept in memory, never recorded by the game.</td>
              <td>One minute for sign-in, ten minutes for feedback. The hosting provider’s technical logs follow its own policy.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>Legal bases</h2>
      <ul>
        <li>
          <strong>Performance of the service</strong> you request by creating an account: account, games, ranking.
        </li>
        <li>
          <strong>Legitimate interest</strong>: security of the service (limiting sign-in attempts and feedback submissions), improving the game
          from the feedback you choose to send, and statistical measurement of where visits come from. You can object at any time by turning on
          “Do Not Track” in your browser.
        </li>
      </ul>

      <h2>What is public</h2>
      <p>
        Your username, Elo rating and tier, statistics, ranked results and head-to-head records appear in the ranking and on your profile,
        visible to everyone. Games against the AI are not ranked. A tournament’s name, its participants’ usernames, its bracket and its results are
        visible to anyone who knows its code. Your feedback is never published.
      </p>

      <h2>Storage in your browser</h2>
      <p>
        The website does not use cookies. It stores in the browser’s local storage your session token, to keep you signed in, and your display
        preferences (language, sound), plus a small counter that avoids asking for your feedback too often after a game: this storage is strictly
        necessary for the service and therefore does not require consent. The tab’s session storage keeps, until it is closed, a random
        identifier of the visit so that it is only counted once; it is not kept from one visit to the next.
      </p>

      <h2>Recipients and processors</h2>
      <p>Your data is neither sold nor shared for commercial purposes. It is processed by:</p>
      <ul>
        <li>
          <strong>{LEGAL.host.name}</strong> (United States): hosting of the server and the database;
        </li>
        <li>
          <strong>Cloudflare, Inc.</strong> (United States): domain name management and, where applicable, routing of traffic;
        </li>
        <li>
          <strong>Telegram</strong> (messaging) and <strong>Resend, Inc.</strong> (United States, email delivery), where applicable: delivering to the
          publisher a notification of each feedback received.
        </li>
      </ul>
      <p>
        These transfers outside the European Union are covered by the safeguards provided for by the GDPR (standard contractual clauses of the
        European Commission or the EU–US Data Privacy Framework, depending on the provider).
      </p>

      <h2>Security</h2>
      <p>
        Encrypted connections (HTTPS), passwords and tokens stored as hashes, limited sign-in attempts, database access restricted to the
        hosting environment.
      </p>

      <h2>Your rights</h2>
      <p>
        You have the right to access, rectify, erase, restrict, port and object to the processing of your data. To exercise them — in
        particular to delete your account — write to <Mail /> from an address that allows you to be identified, stating your username. You
        will receive an answer within one month.
      </p>
      <p>
        You may also lodge a complaint with the CNIL, the French data protection authority (
        <a href="https://www.cnil.fr" rel="noopener noreferrer" target="_blank">
          cnil.fr
        </a>
        ).
      </p>
    </LegalShell>
  )
}
